import {
  MILLISECONDS_PER_DAY,
  MILLISECONDS_PER_WEEK,
  type ScanPhotoTimelinePeriod,
  type ScanPhotoTimelineRow,
} from '@kaiizen/kai-scan-template-system';

import { getPhotoCountLabel } from './progressAltModel';
import { type ProgressAltRowNumber, UNDATED_ROW_NUMBER } from './progressAltViewUrl';

/**
 * How a timeline row names itself.
 *
 * Two surfaces read these: the date column down the side of the grid, and the time navigator under
 * the photo view. They have to agree — the whole point of the navigator's label is that the reader
 * recognises it as the row they clicked — so the wording is worked out once, here.
 */

const DATE_FORMAT: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };
const DATE_NO_WEEKDAY_FORMAT: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
const MONTH_FORMAT: Intl.DateTimeFormatOptions = { month: 'short', year: 'numeric' };
const TIME_FORMAT: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };

export const UNDATED_ROW_DATE_LABEL = 'No date';

/** Stands in for the ordinal the undated row hasn't got: not a bucket that was skipped, but no bucket. */
export const UNDATED_ROW_NUMBER_LABEL = '⊘';

/** Which bucket the numbering counts from — the scan's first photo-bearing one, undated aside. */
export const getFirstPeriodStart = (rows: readonly ScanPhotoTimelineRow[]): number => rows.find(row => row.periodStart != null)?.periodStart ?? 0;

/**
 * Days, weeks and months are numbered from the scan's first photo-bearing bucket rather than from
 * the calendar, and counted as elapsed rather than ranked — skip a fortnight and week 1 is followed
 * by week 4, not week 2, so the gap stays visible.
 */
const getElapsedCount = (periodStart: number, firstPeriodStart: number, spanMilliseconds: number) =>
  Math.round((periodStart - firstPeriodStart) / spanMilliseconds) + 1;

const getElapsedMonths = (periodStart: number, firstPeriodStart: number) => {
  const first = new Date(firstPeriodStart);
  const current = new Date(periodStart);
  return (current.getFullYear() - first.getFullYear()) * 12 + (current.getMonth() - first.getMonth()) + 1;
};

/**
 * How far into the scan the row sits, counted in the unit its own label names — which is why the two
 * are worked out in one place. A collapsed date column shows this number and nothing else, so it has
 * to be the same number the full label leads with.
 */
export const getRowPeriodNumber = (
  periodStart: number,
  period: ScanPhotoTimelinePeriod,
  firstPeriodStart: number,
) => {
  if (period.unit === 'month') return getElapsedMonths(periodStart, firstPeriodStart);
  if (period.milliseconds >= MILLISECONDS_PER_WEEK) {
    return getElapsedCount(periodStart, firstPeriodStart, MILLISECONDS_PER_WEEK);
  }
  if (period.milliseconds < MILLISECONDS_PER_DAY) {
    return getElapsedCount(periodStart, firstPeriodStart, period.milliseconds);
  }
  return getElapsedCount(periodStart, firstPeriodStart, MILLISECONDS_PER_DAY);
};

export const getRowDateLabel = (
  periodStart: number,
  period: ScanPhotoTimelinePeriod,
  firstPeriodStart: number,
) => {
  const date = new Date(periodStart);
  const number = getRowPeriodNumber(periodStart, period, firstPeriodStart);
  if (period.unit === 'month') {
    return `Month #${number}, ${date.toLocaleDateString(undefined, MONTH_FORMAT)}`;
  }
  if (period.milliseconds >= MILLISECONDS_PER_WEEK) {
    return `Week #${number}, ${date.toLocaleDateString(undefined, DATE_NO_WEEKDAY_FORMAT)}`;
  }
  if (period.milliseconds < MILLISECONDS_PER_DAY) {
    return `${date.toLocaleDateString(undefined, DATE_FORMAT)}, ${date.toLocaleTimeString(undefined, TIME_FORMAT)}`;
  }
  return `Day #${number}, ${date.toLocaleDateString(undefined, DATE_NO_WEEKDAY_FORMAT)}`;
};

/** The row's label, undated row included. */
export const getRowLabel = (
  row: Pick<ScanPhotoTimelineRow, 'periodStart'>,
  period: ScanPhotoTimelinePeriod,
  firstPeriodStart: number,
) =>
  row.periodStart == null
    ? UNDATED_ROW_DATE_LABEL
    : getRowDateLabel(row.periodStart, period, firstPeriodStart);

/** How many photos the row holds, its columns all together. */
const getRowPhotoCount = (cells: ScanPhotoTimelineRow['cells']) => cells.reduce((total, cell) => total + cell.photos.length, 0);

/**
 * What the row holds, to go under the date saying when it holds it.
 *
 * The undated row spells out that its photos have no date, having no date above the count to say so —
 * which is the line it carried before it carried a count at all.
 *
 * Every column of the row counts once: a photo attached to both a group and one of its scoped tasks
 * belongs to the task's cell alone, so summing across the row double-counts nothing.
 */
export const getRowPhotoCountLabel = (row: Pick<ScanPhotoTimelineRow, 'periodStart' | 'cells'>) =>
  getPhotoCountLabel(getRowPhotoCount(row.cells), row.periodStart == null ? 'undated' : undefined);

/**
 * Which row this is: the ordinal its label leads with, or the undated row's stand-in.
 *
 * The row's whole identity as far as anything outside the timeline is concerned — what the URL folds
 * by and what the collapse state is kept under — so it is the same number the reader sees, and a
 * folded row can be named in a link by reading it off the screen.
 */
export const getRowNumber = (
  row: Pick<ScanPhotoTimelineRow, 'periodStart'>,
  period: ScanPhotoTimelinePeriod,
  firstPeriodStart: number,
): ProgressAltRowNumber =>
  row.periodStart == null
    ? UNDATED_ROW_NUMBER
    : getRowPeriodNumber(row.periodStart, period, firstPeriodStart);

/** The row's ordinal alone, for a column too narrow to carry the whole label. */
export const getRowNumberLabel = (
  row: Pick<ScanPhotoTimelineRow, 'periodStart'>,
  period: ScanPhotoTimelinePeriod,
  firstPeriodStart: number,
) => {
  const rowNumber = getRowNumber(row, period, firstPeriodStart);
  return rowNumber === UNDATED_ROW_NUMBER ? UNDATED_ROW_NUMBER_LABEL : `${rowNumber}`;
};
